import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { localizedPath } from '../../../utils/localizedPath';

/**
 * The internal-linking block from docs/SEO-CONTENT.md.
 *
 * One link per target per page, anchor text is the target's primary keyword in
 * the page's own language, and language stays in language. The Moodle page is
 * the positioning, so every other page links to it; nothing is orphaned.
 */
const CASE_ESTUDIOS = {
  key: 'caseEstudios',
  en: 'Estudios Tributarios case study',
  es: 'caso Estudios Tributarios',
};
const CASE_PROSPERIA = { key: 'caseProsperia', en: 'Prosperia case study', es: 'caso Prosperia' };
const GLOSSARY = { key: 'glossary', en: 'AI agent glossary', es: 'glosario de agentes de IA' };
const MOODLE = { key: 'moodleAgent', en: 'Moodle AI agent', es: 'agente de IA para Moodle' };

const LINKS = {
  home: [
    { key: 'moodleAgent', en: 'Moodle AI agent', es: 'agente de IA para Moodle' },
    CASE_ESTUDIOS,
    {
      key: 'whatsappAgent',
      en: 'WhatsApp and Web AI agents',
      es: 'agentes de IA para WhatsApp y Web',
    },
    { key: 'agenticPipelines', en: 'agentic pipelines', es: 'pipelines de agentes' },
    { key: 'about', en: 'about E-comienza', es: 'sobre E-comienza' },
  ],
  moodleAgent: [
    CASE_ESTUDIOS,
    GLOSSARY,
    { key: 'agenticPipelines', en: 'agentic pipelines', es: 'pipelines de agentes' },
    { key: 'about', en: 'about E-comienza', es: 'sobre E-comienza' },
  ],
  whatsappAgent: [
    { key: 'moodleAgent', en: 'Moodle AI agent', es: 'agente de IA para Moodle' },
    { key: 'about', en: 'about E-comienza', es: 'sobre E-comienza' },
  ],
  agenticPipelines: [
    { key: 'moodleAgent', en: 'Moodle AI agent', es: 'agente de IA para Moodle' },
    { key: 'whatsappAgent', en: 'WhatsApp AI agent', es: 'agente de IA para WhatsApp' },
    { key: 'about', en: 'about E-comienza', es: 'sobre E-comienza' },
  ],
  about: [
    { key: 'moodleAgent', en: 'Moodle AI agent', es: 'agente de IA para Moodle' },
    { key: 'whatsappAgent', en: 'WhatsApp AI agent', es: 'agente de IA para WhatsApp' },
    { key: 'agenticPipelines', en: 'agentic pipelines', es: 'pipelines de agentes' },
  ],
  // The cases and the glossary all point back at the Moodle page: it is the
  // positioning, and nothing on the site is a dead end.
  caseEstudios: [MOODLE, CASE_PROSPERIA, GLOSSARY],
  caseProsperia: [
    MOODLE,
    { key: 'whatsappAgent', en: 'WhatsApp AI agent', es: 'agente de IA para WhatsApp' },
    CASE_ESTUDIOS,
  ],
  glossary: [MOODLE, CASE_ESTUDIOS],
};

const labels = {
  en: { title: 'Keep reading', nav: 'Related pages' },
  es: { title: 'Sigue leyendo', nav: 'Páginas relacionadas' },
};

const propTypes = {
  /** Route key of the page the links are rendered on. */
  from: PropTypes.string.isRequired,
};

const RelatedPages = ({ from, className, ...props }) => {
  const { lang } = useLanguage();
  const links = LINKS[from] || [];
  if (!links.length) return null;

  const t = labels[lang] || labels.en;

  return (
    <nav
      {...props}
      className={classNames('related-pages', className)}
      aria-label={t.nav}
    >
      <span className="section-label">{t.title}</span>
      <ul className="list-reset">
        {links.map((link) => (
          <li key={link.key}>
            <Link to={localizedPath(link.key, lang)}>{link[lang] || link.en}</Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

RelatedPages.propTypes = propTypes;

export default RelatedPages;
